import { useState, useEffect } from 'react';
import { IconRefresh, IconSearch, IconReceipt, IconCreditCard, IconLoader2, IconX } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

interface Distributor {
  id: number;
  i_customer: number;
  company_name: string;
  code: string | null;
  xero_contact_id: string;
}

interface XeroInvoice {
  id: string;
  invoiceNumber: string;
  reference: string;
  type: 'ACCREC' | 'ACCPAY';
  status: string;
  lineAmountTypes: string;
  subTotal: number;
  totalTax: number;
  total: number;
  currencyCode: string;
  currencyRate: number;
  date: string;
  dueDate: string;
  contact: {
    id: string;
    name: string;
  };
  lineItems: Array<{
    id: string;
    description: string;
    quantity: number;
    unitAmount: number;
    lineAmount: number;
    taxType: string;
    taxAmount: number;
  }>;
}

interface XeroPayment {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  date: string;
  amount: number;
  currencyCode: string;
  currencyRate: number;
  reference: string;
  status: string;
  paymentType: 'ACCRECPAYMENT' | 'ACCPAYPAYMENT';
  account: {
    id: string;
    code: string;
  };
}

interface XeroStatement {
  contact: {
    id: string;
    name: string;
    email: string;
  };
  statementDate: string;
  openingBalance: number;
  closingBalance: number;
  invoices: XeroInvoice[];
  payments: XeroPayment[];
  summary: {
    totalInvoiced: number;
    totalPaid: number;
    totalOutstanding: number;
    invoiceCount: number;
    paymentCount: number;
  };
}

const TENANT_ID = '2130c79b-608e-45ef-a5dd-801bc5d53cf8';

export function XeroStatementsPage() {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const [loadingDistributors, setLoadingDistributors] = useState(false);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [statement, setStatement] = useState<XeroStatement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [comboboxOpen, setComboboxOpen] = useState(false);

  useEffect(() => {
    loadDistributors();
  }, []);

  const loadDistributors = async (search?: string) => {
    setLoadingDistributors(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await api.get(`/distributors/list/autocomplete${params}`);
      setDistributors(res.data.data || []);
    } catch (err: any) {
      console.error('Error loading distributors:', err);
      setError('Failed to load distributors');
    } finally {
      setLoadingDistributors(false);
    }
  };

  const loadStatement = async () => {
    if (!selectedDistributor || !selectedDistributor.xero_contact_id) {
      setError('Please select a distributor with a Xero contact ID');
      return;
    }

    setLoadingStatement(true);
    setError(null);
    setStatement(null);

    try {
      const res = await api.get(
        `/xero/statement?tenantId=${TENANT_ID}&contactId=${selectedDistributor.xero_contact_id}`
      );
      if (res.data.success && res.data.data) {
        setStatement(res.data.data);
      } else {
        setError(res.data.message || 'Failed to load statement');
      }
    } catch (err: any) {
      console.error('Error loading statement:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to load statement from Xero via API Gateway'
      );
    } finally {
      setLoadingStatement(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const match = dateString.match(/\/Date\((\d+)/);
      if (match) {
        const timestamp = parseInt(match[1], 10);
        return new Date(timestamp).toLocaleDateString();
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number, currencyCode: string = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getInvoiceTypeLabel = (type: string) => {
    switch (type) {
      case 'ACCREC':
        return 'We Invoice Them';
      case 'ACCPAY':
        return 'They Invoice Us';
      default:
        return type;
    }
  };

  const getInvoiceTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'ACCREC':
        return 'default' as const;
      case 'ACCPAY':
        return 'secondary' as const;
      default:
        return 'outline' as const;
    }
  };

  const getPaymentTypeLabel = (type: string) => {
    switch (type) {
      case 'ACCRECPAYMENT':
        return 'They Pay Us';
      case 'ACCPAYPAYMENT':
        return 'We Pay Them';
      default:
        return type;
    }
  };

  const getPaymentTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'ACCRECPAYMENT':
        return 'success' as const;
      case 'ACCPAYPAYMENT':
        return 'warning' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PAID':
        return 'success' as const;
      case 'AUTHORISED':
        return 'info' as const;
      case 'DRAFT':
        return 'outline' as const;
      default:
        return 'warning' as const;
    }
  };

  const getTransactionImpact = (transaction: { type: 'invoice' | 'payment'; data: XeroInvoice | XeroPayment }): number => {
    if (transaction.type === 'invoice') {
      const inv = transaction.data as XeroInvoice;
      return inv.type === 'ACCREC' ? -inv.total : inv.total;
    } else {
      const pay = transaction.data as XeroPayment;
      return pay.paymentType === 'ACCRECPAYMENT' ? pay.amount : -pay.amount;
    }
  };

  const allTransactions = statement
    ? [
        ...statement.invoices.map((inv) => ({
          type: 'invoice' as const,
          data: inv,
          date: inv.date || '',
        })),
        ...statement.payments.map((pay) => ({
          type: 'payment' as const,
          data: pay,
          date: pay.date || '',
        })),
      ].sort((a, b) => {
        const dateA = (a.date && typeof a.date === 'string') ? a.date.match(/\/Date\((\d+)/)?.[1] || '0' : '0';
        const dateB = (b.date && typeof b.date === 'string') ? b.date.match(/\/Date\((\d+)/)?.[1] || '0' : '0';
        return parseInt(dateA, 10) - parseInt(dateB, 10);
      })
    : [];

  const transactionsWithBalance = statement
    ? (() => {
        let runningBalance = statement.openingBalance;
        return allTransactions.map((transaction) => {
          const impact = getTransactionImpact(transaction);
          runningBalance = runningBalance + impact;
          
          return {
            ...transaction,
            balance: runningBalance,
            impact,
          };
        });
      })()
    : [];

  const displayTransactions = [...transactionsWithBalance].reverse();

  return (
    <div>
      <PageHeader
        title="Xero Statements"
        subtitle="View account statements for distributors via API Gateway"
      />

      {/* Distributor Selection */}
      <MainCard className="mb-6">
        <div className="flex flex-col gap-4">
          <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
            <PopoverTrigger
              className={cn(
                'flex w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm',
                'hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring',
                !selectedDistributor && 'text-muted-foreground'
              )}
            >
              <div className="flex items-center gap-2">
                <IconSearch className="size-4 text-muted-foreground" />
                <span>
                  {selectedDistributor
                    ? `${selectedDistributor.company_name}${selectedDistributor.code ? ` (${selectedDistributor.code})` : ''}`
                    : 'Search by company name or code...'}
                </span>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--anchor-width)] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search distributors..."
                  value={searchTerm}
                  onValueChange={(value) => {
                    setSearchTerm(value);
                    loadDistributors(value);
                  }}
                />
                <CommandList>
                  <CommandEmpty>
                    {loadingDistributors ? 'Loading...' : 'No distributors found.'}
                  </CommandEmpty>
                  <CommandGroup>
                    {distributors.map((distributor) => (
                      <CommandItem
                        key={distributor.id}
                        value={String(distributor.id)}
                        data-checked={selectedDistributor?.id === distributor.id || undefined}
                        onSelect={() => {
                          setSelectedDistributor(distributor);
                          setStatement(null);
                          setError(null);
                          setComboboxOpen(false);
                        }}
                      >
                        <div>
                          <p className="text-sm">{distributor.company_name}</p>
                          {distributor.code && (
                            <p className="text-xs text-muted-foreground">
                              Code: {distributor.code}
                            </p>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {selectedDistributor && (
            <Button
              variant="default"
              className="w-full"
              onClick={loadStatement}
              disabled={loadingStatement || !selectedDistributor.xero_contact_id}
            >
              {loadingStatement ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconRefresh className="size-4" />
              )}
              {loadingStatement ? 'Loading Statement...' : 'Load Statement'}
            </Button>
          )}

          {selectedDistributor && !selectedDistributor.xero_contact_id && (
            <Alert>
              <AlertDescription>
                This distributor does not have a Xero contact ID configured.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </MainCard>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="shrink-0 ml-2">
              <IconX className="size-4" />
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Statement Summary */}
      {statement && (
        <>
          <Card className="mb-6">
            <CardContent>
              <h3 className="text-lg font-semibold mb-4">Statement Summary</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                <div>
                  <span className="text-xs text-muted-foreground">Contact</span>
                  <p className="font-medium">{statement.contact.name}</p>
                  <span className="text-xs text-muted-foreground">
                    {statement.contact.email}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Opening Balance</span>
                  <p className="font-medium">
                    {formatCurrency(statement.openingBalance)}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Closing Balance</span>
                  <p
                    className={cn(
                      'font-medium',
                      statement.closingBalance < 0 ? 'text-red-600' : 'text-green-600'
                    )}
                  >
                    {formatCurrency(statement.closingBalance)}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Statement Date</span>
                  <p className="font-medium">
                    {new Date(statement.statementDate).toLocaleString()}
                  </p>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <span className="text-xs text-muted-foreground">Total Invoiced</span>
                  <p className="text-lg font-semibold">
                    {formatCurrency(statement.summary.totalInvoiced)}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Total Paid</span>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(statement.summary.totalPaid)}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Total Outstanding</span>
                  <p
                    className={cn(
                      'text-lg font-semibold',
                      statement.summary.totalOutstanding < 0 ? 'text-red-600' : 'text-green-600'
                    )}
                  >
                    {formatCurrency(statement.summary.totalOutstanding)}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Transactions</span>
                  <p className="text-lg font-semibold">
                    {statement.summary.invoiceCount} invoices, {statement.summary.paymentCount}{' '}
                    payments
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transactions Table */}
          <MainCard>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayTransactions.map((transaction, idx) => {
                  if (transaction.type === 'invoice') {
                    const inv = transaction.data as XeroInvoice;
                    return (
                      <TableRow key={`invoice-${inv.id}-${idx}`}>
                        <TableCell>{formatDate(inv.date)}</TableCell>
                        <TableCell>
                          <Badge variant={getInvoiceTypeBadgeVariant(inv.type)}>
                            <IconReceipt className="size-3" />
                            {getInvoiceTypeLabel(inv.type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium">{inv.invoiceNumber}</p>
                          {inv.reference && (
                            <p className="text-xs text-muted-foreground">{inv.reference}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          {inv.lineItems.length > 0 ? (
                            <span className="text-sm">
                              {inv.lineItems[0].description}
                              {inv.lineItems.length > 1 && ` (+${inv.lineItems.length - 1} more)`}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">No description</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(inv.status)}>
                            {inv.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              'text-sm font-medium',
                              inv.type === 'ACCREC' && 'text-green-600',
                              inv.type === 'ACCPAY' && 'text-red-600'
                            )}
                          >
                            {inv.type === 'ACCREC' ? '+' : '-'}
                            {formatCurrency(inv.total, inv.currencyCode)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              'text-sm font-medium',
                              transaction.balance < 0 && 'text-red-600',
                              transaction.balance > 0 && 'text-green-600'
                            )}
                          >
                            {formatCurrency(transaction.balance, inv.currencyCode)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  } else {
                    const pay = transaction.data as XeroPayment;
                    return (
                      <TableRow key={`payment-${pay.id}-${idx}`}>
                        <TableCell>{formatDate(pay.date)}</TableCell>
                        <TableCell>
                          <Badge variant={getPaymentTypeBadgeVariant(pay.paymentType)}>
                            <IconCreditCard className="size-3" />
                            {getPaymentTypeLabel(pay.paymentType)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium">{pay.invoiceNumber}</p>
                          {pay.reference && (
                            <p className="text-xs text-muted-foreground">{pay.reference}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">Payment</span>
                          <p className="text-xs text-muted-foreground">
                            Account: {pay.account.code}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(pay.status)}>
                            {pay.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              'text-sm font-medium',
                              pay.paymentType === 'ACCRECPAYMENT' && 'text-green-600',
                              pay.paymentType === 'ACCPAYPAYMENT' && 'text-red-600'
                            )}
                          >
                            {pay.paymentType === 'ACCRECPAYMENT' ? '+' : '-'}
                            {formatCurrency(pay.amount, pay.currencyCode)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              'text-sm font-medium',
                              transaction.balance < 0 && 'text-red-600',
                              transaction.balance > 0 && 'text-green-600'
                            )}
                          >
                            {formatCurrency(transaction.balance, pay.currencyCode)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  }
                })}
              </TableBody>
            </Table>
          </MainCard>
        </>
      )}
    </div>
  );
}
